import User from "core/models/user";
import FreshDeskStore from "core/stores/fresh-desk.store";
import { Ticket, TicketComment } from "core/types/ticket";
import BaseServiceWithUserTracking from "./base-with-user-tracking.service";
import { activityLogArea } from "./user-tracking-helper";
//See https://developers.freshdesk.com/api/

export type Contact = {
    id: number,
    name: string,
    email: string,
    company_id: number,
    customFields: any,
    created_at: Date,
    updated_at: Date
}

export class FreshDeskService extends BaseServiceWithUserTracking {

    constructor() {
        super(activityLogArea.freskDesk);
        this.setBaseUrl("freshdesk");
    }

    private _getAuth() {
        if (!FreshDeskStore.cache.isCached) {
            throw new Error("Unable to get customer details from freshdesk");
        }

        return FreshDeskStore.cache.value;
    }

    private _getCompanyId() {
        const auth = this._getAuth();

        return auth.company_id;
    }

    getContactAsync(fullName: string | undefined = undefined) {
        const user = User.instance;

        fullName = fullName ?? user?.fullName;

        const request = {
            customerNumber: this.customerNumber,
            email: user?.email,
            name: fullName
        }

        return this.createPromiseRequest("GetContact").post<Contact>(request);
    }

    createReplyAsync(ticketId: string, reply: any) {
        const auth = this._getAuth();

        if (auth?.id <= 0) {
            throw new Error("Need user id");
        }

        const hasAttachments = reply.attachments?.length > 0 ?? false;
        reply["user_id"] = auth.id;

        let data = reply;

        if (hasAttachments) {
            data = new FormData();
            reply.attachments.forEach((file: any) => {
                data.append("attachments[]", file.file);
            });

            data.append("body", reply.body);
            data.append("user_id", reply.user_id);
        }

        const requestHelper = this.createPromiseRequest(`tickets/${ticketId}/reply`);

        return hasAttachments
            ? requestHelper.formPost(data)
            : requestHelper.post(data);
    }

    getTicketConversationsAsync(ticketId: string) {
        return this.createPromiseRequest(`tickets/${ticketId}/conversations`).get<TicketComment[]>();
    }

    getTicketAsync(ticketId: string) {
        return this.createPromiseRequest(`tickets/${ticketId}`).get<Ticket>();
    }

    getTicketsAsync() {
        const params = { companyId: this._getCompanyId() };

        return this.createPromiseRequest("tickets")
            .withParams(params)
            .get<Ticket[]>();
    }

    createTicketAsync(newTicket: any) {
        if (newTicket === null || newTicket === undefined) {
            throw new Error("No ticket supplied");
        }

        const companyId = this._getCompanyId(); //might not be needed as in newTicket
        const auth = this._getAuth();
        const email = User.instance?.email ?? "";
        const userName = auth.name;

        const hasAttachments = newTicket?.attachments?.length > 0 ?? false;

        const requestHelper = this.createPromiseRequest("tickets")

        if (!hasAttachments) {
            const data = {
                company_id: companyId,
                requester_id: auth.id,
                email: email,
                subject: newTicket.subject,
                description: newTicket.description,
                source: newTicket.source,
                status: newTicket.status,
                group_id: newTicket.group_id,
                priority: newTicket.priority,
                custom_fields: {
                    cf_created_by: userName,
                    ...newTicket.custom_fields,
                }
            };

            return requestHelper.post(data);
        }

        const formData = new FormData();

        newTicket.attachments.forEach((file: any) => {
            formData.append("attachments[]", file.file);
        });

        formData.append("company_id", companyId);
        formData.append("email", email);
        formData.append("requester_id", auth.id);
        formData.append("subject", newTicket.subject);
        formData.append("description", newTicket.description);
        formData.append("source", newTicket.source);
        formData.append("status", newTicket.status);
        formData.append("group_id", newTicket.group_id);
        formData.append("priority", newTicket.priority);
        formData.append("custom_fields[cf_created_by]", userName);
        formData.append("custom_fields[cf_d4b_query_type]", newTicket.custom_fields["cf_d4b_query_type"]);

        return requestHelper.formPost(formData);
    }
}
